'use client'

import { useState } from 'react'
import { Input } from '@/app/[locale]/(ui)/input'
import { useTranslations } from 'next-intl'
import type { Faq } from '@/types/faq'

export const AllFaqsFiltered = ({ faq }: { faq: Faq[] }) => {
  const t = useTranslations()
  const [searchInput, setSearchInput] = useState('')

  const isFaqRelevant = ({ question, answer }: { question: string; answer: string }) =>
    !searchInput.length ||
    (searchInput.match(/[\p{L}\p{N}]+/gu) ?? []).every(
      (word) =>
        question.toLowerCase().includes(word.toLowerCase()) || answer.toLowerCase().includes(word.toLowerCase()),
    )

  const questionsGeneral = faq.filter((faq) => faq.category === 'general' && isFaqRelevant(faq))
  const questionsBag = faq.filter((faq) => faq.category === 'bag' && isFaqRelevant(faq))
  const questionsContainer = faq.filter((faq) => faq.category === 'container' && isFaqRelevant(faq))
  const questionsPukk = faq.filter((faq) => faq.category === 'gravel' && isFaqRelevant(faq))
  const hasResults =
    questionsGeneral.length > 0 || questionsBag.length > 0 || questionsContainer.length > 0 || questionsPukk.length > 0

  return (
    <div className="space-y-10 bg-gray-100 px-6 py-16 sm:px-8 md:py-20 xl:rounded-2xl">
      <div className="space-y-10">
        <div className="space-y-8">
          <h2 className="flex items-center gap-2 text-3xl font-bold md:text-4xl">{t('FAQ_title')}</h2>
          <Input
            label={t('FAQ_filter_label')}
            type="search"
            className="md:max-w-xl"
            placeholder={t('FAQ_filter_placeholder')}
            value={searchInput}
            onChange={({ target }) => setSearchInput(target.value)}
          />
        </div>

        {!hasResults && <p className="text-lg">{t('FAQ_no_results')}</p>}

        <div className="grid gap-x-10 gap-y-20 md:grid-cols-2">
          {questionsGeneral.length > 0 && (
            <div className="flex flex-col gap-4 overflow-hidden">
              <h2 className="text-xl font-bold md:text-2xl">{t('FAQ_general')}</h2>
              {questionsGeneral.map((item, index) => (
                <details key={index} className="max-w-full lg:w-[1000px]">
                  <summary className="cursor-pointer select-none font-semibold sm:text-lg">{item.question}</summary>
                  <div
                    className="p-4 py-0 text-sm sm:text-base [&_a]:underline [&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-4"
                    dangerouslySetInnerHTML={{ __html: item.answer }}
                  />
                </details>
              ))}
            </div>
          )}

          {questionsBag.length > 0 && (
            <div className="flex flex-col gap-4 overflow-hidden">
              <h2 className="text-xl font-bold md:text-2xl">{t('FAQ_bags')}</h2>
              {questionsBag.map((item, index) => (
                <details key={index} className="max-w-full lg:w-[1000px]">
                  <summary className="cursor-pointer select-none font-semibold sm:text-lg">{item.question}</summary>
                  <div
                    className="p-4 py-0 text-sm sm:text-base [&_a]:underline [&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-4"
                    dangerouslySetInnerHTML={{ __html: item.answer }}
                  />
                </details>
              ))}
            </div>
          )}
          {questionsContainer.length > 0 && (
            <div className="flex flex-col gap-4 overflow-hidden">
              <h2 className="text-xl font-bold md:text-2xl">{t('FAQ_containers')}</h2>
              {questionsContainer.map((item, index) => (
                <details key={index} className="max-w-full lg:w-[1000px]">
                  <summary className="cursor-pointer select-none font-semibold sm:text-lg">{item.question}</summary>
                  <div
                    className="p-4 py-0 text-sm sm:text-base [&_a]:underline [&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-4"
                    dangerouslySetInnerHTML={{ __html: item.answer }}
                  />
                </details>
              ))}
            </div>
          )}

          {questionsPukk.length > 0 && (
            <div className="flex flex-col gap-4 overflow-hidden">
              <h2 className="text-xl font-bold md:text-2xl">{t('FAQ_gravel')}</h2>
              {questionsPukk.map((item, index) => (
                <details key={index} className="max-w-full lg:w-[1000px]">
                  <summary className="cursor-pointer select-none font-semibold sm:text-lg">{item.question}</summary>
                  <div
                    className="p-4 py-0 text-sm sm:text-base [&_a]:underline [&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-4"
                    dangerouslySetInnerHTML={{ __html: item.answer }}
                  />
                </details>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
